import React, { useState, useRef, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { format, parse, isValid, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangeFilter.scss";

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/**
 * Attempts to parse a week label string into a Date object.
 * Handles formats like "2024-01-15", "01/15/2024", "1/15/2024", "(Week of 2024-01-15)", etc.
 */
const parseWeekLabel = (weekLabel) => {
  if (!weekLabel) return null;

  // Remove "(Week of ...)" wrapper if present
  let cleaned = String(weekLabel).trim();
  const weekOfMatch = cleaned.match(/\(?Week\s+of\s+(.+?)\)?$/i);
  if (weekOfMatch) {
    cleaned = weekOfMatch[1].trim();
  }

  // Remove parentheses
  cleaned = cleaned.replace(/[()]/g, "").trim();

  // Try common date formats
  const formats = [
    "yyyy-MM-dd",
    "MM/dd/yyyy",
    "M/d/yyyy",
    "MM-dd-yyyy",
    "yyyy/MM/dd",
    "dd-MM-yyyy",
    "dd/MM/yyyy",
  ];

  for (const fmt of formats) {
    try {
      const parsed = parse(cleaned, fmt, new Date());
      if (isValid(parsed) && parsed.getFullYear() > 2000) {
        return parsed;
      }
    } catch {
      // ignore
    }
  }

  // Fallback: native Date parse
  const fallback = new Date(cleaned);
  if (isValid(fallback) && fallback.getFullYear() > 2000) {
    return fallback;
  }

  return null;
};

/**
 * DateRangeFilter component — calendar dropdown for filtering graph data by date range.
 *
 * @param {Object}   props
 * @param {string[]} props.weeks - Array of week labels from the graph data
 * @param {function} props.onFilterChange - Called with { startDate, endDate, filteredIndices }
 */
const DateRangeFilter = ({ weeks = [], onFilterChange, initialStartDate = null, initialEndDate = null }) => {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Compute available date range from weeks (memoized to keep stable reference for useEffect)
  const parsedDates = useMemo(
    () =>
      weeks
        .map((w, i) => ({ date: parseWeekLabel(w), index: i }))
        .filter((d) => d.date !== null),
    [weeks]
  );

  const minDate = useMemo(
    () =>
      parsedDates.length > 0
        ? new Date(Math.min(...parsedDates.map((d) => d.date.getTime())))
        : undefined,
    [parsedDates]
  );

  const maxDate = useMemo(
    () =>
      parsedDates.length > 0
        ? new Date(Math.max(...parsedDates.map((d) => d.date.getTime())))
        : undefined,
    [parsedDates]
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Compute filtered indices whenever dates change
  useEffect(() => {
    if (!onFilterChange) return;

    if (!startDate && !endDate) {
      // No filter — return all indices
      onFilterChange({
        startDate: null,
        endDate: null,
        filteredIndices: null, // null means "show all"
      });
      return;
    }

    const filteredIndices = parsedDates
      .filter(({ date }) => {
        const d = startOfDay(date);
        if (startDate && endDate) {
          return isWithinInterval(d, {
            start: startOfDay(startDate),
            end: endOfDay(endDate),
          });
        }
        if (startDate) {
          return d >= startOfDay(startDate);
        }
        return true;
      })
      .map(({ index }) => index);

    onFilterChange({
      startDate,
      endDate,
      filteredIndices,
    });
  }, [startDate, endDate, parsedDates, onFilterChange]);

  // Timestamps (midnight) for the individual dates that actually have data
  const validDates = useMemo(
    () => new Set(parsedDates.map(({ date }) => startOfDay(date).getTime())),
    [parsedDates]
  );

  /**
   * filterDate for react-datepicker:
   * - Only dates that actually have data are ever selectable
   * - After a start is chosen, the end date must also be on/after it
   */
  const filterDate = (date) => {
    if (!validDates.has(startOfDay(date).getTime())) return false;
    return !startDate || date >= startOfDay(startDate);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;

    setStartDate(start ? startOfDay(start) : null);
    setEndDate(end ? endOfDay(end) : null);

    // Close dropdown once both ends of the range are picked
    if (start && end) {
      setTimeout(() => setIsOpen(false), 300);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setStartDate(null);
    setEndDate(null);
  };

  const displayText = () => {
    if (startDate && endDate) {
      return `${format(startDate, "MMM d, yyyy")} – ${format(endDate, "MMM d, yyyy")}`;
    }
    if (startDate) {
      return `${format(startDate, "MMM d, yyyy")} – ...`;
    }
    return "Select date range";
  };

  return (
    <div className="date-range-filter" ref={containerRef}>
      <button
        type="button"
        className="date-range-filter__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="date-range-filter__text">{displayText()}</span>
        <span className="date-range-filter__icon">
          <CalendarIcon />
        </span>
      </button>

      {(startDate || endDate) && (
        <button
          type="button"
          className="date-range-filter__clear"
          onClick={handleClear}
          title="Clear filter"
        >
          ✕
        </button>
      )}

      {isOpen && (
        <div className="date-range-filter__dropdown">
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            minDate={minDate}
            maxDate={maxDate}
            calendarClassName="date-range-filter__calendar"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            filterDate={filterDate}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
export { parseWeekLabel };
